/**
 * Unified Analytics Service
 *
 * This module provides a unified interface for tracking events across multiple
 * analytics platforms (RudderStack and Google Tag Manager/Analytics).
 *
 * Events are automatically sent to both platforms when tracked through this service.
 */

import * as RudderAnalytics from './analytics';
import * as GTMAnalytics from './gtm';

// Re-export event names for convenience
export { ANALYTICS_EVENTS } from './analytics';

// ==================== User Identification ====================

/**
 * Identify a user across all analytics platforms
 */
export const identifyUser = (userId: string, traits?: Record<string, any>): void => {
  // RudderStack identification
  RudderAnalytics.identifyUser(userId, traits);

  // GTM user properties
  GTMAnalytics.setGTMUserProperties({
    user_id: userId,
    ...traits,
  });
};

// ==================== Authentication Events ====================

/**
 * Track user login
 */
export const trackUserLogin = (email: string, userId: string, method: string = 'email'): void => {
  // RudderStack
  RudderAnalytics.trackUserLogin(email, userId);

  // GTM
  GTMAnalytics.trackGTMUserLogin(method, userId);
};

/**
 * Track account creation
 */
export const trackAccountCreated = (email: string, userId: string, method: string = 'email'): void => {
  // RudderStack
  RudderAnalytics.trackAccountCreated(email, userId);

  // GTM
  GTMAnalytics.trackGTMSignUp(method, userId);
};

// ==================== Coupon Events ====================

/**
 * Track coupon submission
 */
export const trackCouponSubmitted = (
  couponCode: string,
  email?: string,
  userId?: string,
  isValid?: boolean
): void => {
  // RudderStack
  RudderAnalytics.trackCouponSubmitted(couponCode, email, userId, isValid);

  // GTM
  GTMAnalytics.trackGTMEvent('coupon_submitted', {
    coupon_code: couponCode,
    email,
    user_id: userId,
    is_valid: isValid,
  });
};

/**
 * Track promo code submission
 */
export const trackPromoCodeSubmitted = (
  promoCode: string,
  email?: string,
  userId?: string,
  isValid?: boolean,
  errorMessage?: string
): void => {
  // RudderStack
  RudderAnalytics.trackPromoCodeSubmitted(promoCode, email, userId, isValid, errorMessage);

  // GTM
  GTMAnalytics.trackGTMEvent('promo_code_submitted', {
    promo_code: promoCode,
    email,
    user_id: userId,
    is_valid: isValid,
    error_message: errorMessage,
  });
};

/**
 * Track successful promo code redemption
 */
export const trackPromoCodeRedeemedSuccess = (
  promoCode: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackPromoCodeRedeemedSuccess(promoCode, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('promo_code_redeemed_success', {
    promo_code: promoCode,
    email,
    user_id: userId,
  });
};

/**
 * Track failed promo code redemption
 */
export const trackPromoCodeRedeemedFailed = (
  promoCode: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackPromoCodeRedeemedFailed(promoCode, errorMessage, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('promo_code_redeemed_failed', {
    promo_code: promoCode,
    error_message: errorMessage,
    email,
    user_id: userId,
  });
};

// ==================== Co-branded Web Flow Events ====================

/**
 * Track a web-flow sign up (co-branded deal). `dealId` ties the event to the
 * deal for Mixpanel/Everflow attribution.
 */
export const trackWebSignUp = (
  dealId?: string,
  email?: string,
  userId?: string
): void => {
  trackCustomEvent('Web_sign_up', {
    deal_id: dealId,
    email,
    user_id: userId,
  });
};

/**
 * Track a click on an app store / "Download the App" link. `location` notes
 * where the click happened (e.g. 'redeem_success', 'subscription').
 */
export const trackStoreLinkClick = (
  location?: string,
  dealId?: string,
  userId?: string
): void => {
  trackCustomEvent('store_link_click', {
    location,
    deal_id: dealId,
    user_id: userId,
  });
};

/**
 * Track a web-flow promo code redemption (co-branded deal).
 */
export const trackWebCodeRedemption = (
  dealId?: string,
  promoCode?: string,
  email?: string,
  userId?: string
): void => {
  trackCustomEvent('Web_code_redemption', {
    deal_id: dealId,
    promo_code: promoCode,
    email,
    user_id: userId,
  });
};

// ==================== Subscription & Billing Events ====================

/**
 * Track plan selection
 */
export const trackPlanSelected = (
  planName: string,
  amount: number,
  currency: string,
  billingInterval: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackPlanSelected(planName, amount, currency, billingInterval, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('plan_selected', {
    plan_name: planName,
    value: amount / 100,
    currency: currency.toUpperCase(),
    billing_interval: billingInterval,
    email,
    user_id: userId,
  });
};

/**
 * Track billing submission
 */
export const trackBillingSubmitted = (
  planName: string,
  amount: number,
  currency: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackBillingSubmitted(planName, amount, currency, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('begin_checkout', {
    plan_name: planName,
    value: amount / 100,
    currency: currency.toUpperCase(),
    email,
    user_id: userId,
  });
};

/**
 * Track successful billing/payment
 */
export const trackBillingSuccess = (
  planName: string,
  amount: number,
  currency: string,
  email?: string,
  userId?: string,
  transactionId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackBillingSuccess(planName, amount, currency, email, userId, transactionId);

  // GTM - using Google Analytics 4 purchase event format
  GTMAnalytics.trackGTMPurchase({
    transactionId: transactionId || `txn_${Date.now()}`,
    value: amount / 100,
    currency: currency.toUpperCase(),
    items: [
      {
        item_id: planName,
        item_name: planName,
        price: amount / 100,
        quantity: 1,
      },
    ],
  });
};

/**
 * Track failed billing/payment
 */
export const trackBillingFailed = (
  planName: string,
  amount: number,
  currency: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackBillingFailed(planName, amount, currency, errorMessage, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('payment_failed', {
    plan_name: planName,
    value: amount / 100,
    currency: currency.toUpperCase(),
    error_message: errorMessage,
    email,
    user_id: userId,
  });
};

/**
 * Track subscription cancellation
 */
export const trackSubscriptionCancelled = (
  planName: string,
  email?: string,
  userId?: string,
  cancellationReason?: string,
  subscriptionId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackSubscriptionCancelled(planName, email, userId, cancellationReason);

  // GTM
  GTMAnalytics.trackGTMCancellation({
    subscriptionId: subscriptionId || 'unknown',
    planName,
    reason: cancellationReason,
  });
};

/**
 * Track failed subscription cancellation
 */
export const trackSubscriptionCancellationFailed = (
  planName: string,
  errorMessage: string,
  email?: string,
  userId?: string
): void => {
  // RudderStack
  RudderAnalytics.trackSubscriptionCancellationFailed(planName, errorMessage, email, userId);

  // GTM
  GTMAnalytics.trackGTMEvent('subscription_cancellation_failed', {
    plan_name: planName,
    error_message: errorMessage,
    email,
    user_id: userId,
  });
};

// ==================== Page Tracking ====================

/**
 * Track page views
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  // GTM
  GTMAnalytics.trackPageView(pagePath, pageTitle);

  // RudderStack page tracking (if needed, can add a page() call)
  // RudderAnalytics currently doesn't have a dedicated page tracking function
};

// ==================== Generic Event Tracking ====================

/**
 * Track a custom event across all platforms
 */
export const trackCustomEvent = (
  eventName: string,
  properties?: Record<string, any>
): void => {
  // RudderStack
  RudderAnalytics.trackEvent(eventName, properties);

  // GTM
  GTMAnalytics.trackGTMEvent(eventName, properties);
};
